import { useRouteMatch } from 'react-router-dom';

import { useDispensary } from 'src/hooks/use-dispensary';
import { useStores } from 'src/hooks/use-stores';
import { useUser } from 'src/hooks/use-user';
import { useEnterpriseMobileAppSettingsQuery } from 'types/graphql';

/**
 * Mirrors the enterpriseId resolution use-feature-flags-controller.ts uses when building the LD
 * context, so this check and the feature flag it's gated behind agree on which enterprise to
 * look at.
 */
function useResolvedEnterpriseId(): string | undefined {
  const dispensary = useDispensary();
  const User = useUser();
  const enterpriseRouteMatch = useRouteMatch<{ enterpriseId: string }>('/enterprise/:enterpriseId');

  return (
    enterpriseRouteMatch?.params.enterpriseId ?? dispensary.retailer?.enterpriseId ?? User.enterpriseId ?? undefined
  );
}

const useHasMobileApp = (): boolean => {
  const { apolloClient } = useStores();
  const enterpriseId = useResolvedEnterpriseId();

  const { data } = useEnterpriseMobileAppSettingsQuery({
    client: apolloClient,
    variables: { enterpriseId: enterpriseId ?? '' },
    skip: !enterpriseId,
  });

  // Use `||`, not `??`: an Android-only (or iOS-only) app can have the other identifier come
  // back as `''` rather than `null`, and `??` only falls through on null/undefined -- it would
  // stop at the empty string and never consult the field that's actually set.
  return Boolean(
    data?.enterpriseMobileAppSettings?.iosBundleIdentifier || data?.enterpriseMobileAppSettings?.androidPackageName
  );
};

/**
 * Same as useHasMobileApp, but also exposes the Apollo query's loading state.
 *
 * Needed when a caller must not make a *persisting* decision based on hasMobileApp before the
 * underlying query has resolved -- useHasMobileApp returns `false` while the query is still
 * in flight, so "no mobile app" and "still loading" are indistinguishable to consumers. The
 * Channels wizard step is one such caller: without `isLoading`, an admin who clicks Continue
 * in the sub-second window before the query resolves would commit the loading-state default
 * and silently drop a previously-persisted `mobile` channel.
 *
 * All other existing callers render-only and don't need this -- they should keep using the
 * plain boolean hook above.
 */
export const useHasMobileAppWithLoading = (): { hasMobileApp: boolean; isLoading: boolean } => {
  const { apolloClient } = useStores();
  const enterpriseId = useResolvedEnterpriseId();

  const { data, loading } = useEnterpriseMobileAppSettingsQuery({
    client: apolloClient,
    variables: { enterpriseId: enterpriseId ?? '' },
    skip: !enterpriseId,
  });

  const hasMobileApp = Boolean(
    data?.enterpriseMobileAppSettings?.iosBundleIdentifier || data?.enterpriseMobileAppSettings?.androidPackageName
  );
  return { hasMobileApp, isLoading: loading };
};

export default useHasMobileApp;
